import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuthFetch } from "@/hooks/useAuthFetch";
import {
  HiOutlinePlus,
  HiOutlineSparkles,
  HiOutlineTrash,
  HiOutlineShare,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineArrowPath,
  HiOutlineEnvelope,
} from "react-icons/hi2";
import { InviteModal } from "@/components/share/InviteModal";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type {
  InterviewQuestion,
  InterviewSettings,
  InterviewStatus,
  QuestionType,
} from "@acde/shared";

const API_URL = import.meta.env.VITE_API_URL || "/api";

// Retry configuration for Cosmos DB eventual consistency
// Increased from 5x800ms to 8x1500ms for more reliable propagation
const PROPAGATION_MAX_ATTEMPTS = 8;
const PROPAGATION_DELAY_MS = 1500;

const QUESTION_TYPES: {
  value: QuestionType;
  label: string;
  description: string;
}[] = [
  { value: "open", label: "Open-ended", description: "Free text response" },
  {
    value: "multiple_choice",
    label: "Multiple Choice",
    description: "Select from options",
  },
  { value: "rating", label: "Rating", description: "1-5 scale" },
  { value: "yes_no", label: "Yes/No", description: "Binary choice" },
];

interface DraftQuestion {
  id: string;
  text: string;
  type: QuestionType;
  required: boolean;
  options: string[];
  aiGenerated: boolean;
  followUpPrompt: string;
}

const DEFAULT_SETTINGS: InterviewSettings = {
  allowVoice: true,
  allowChat: true,
  allowText: true,
  allowFileUpload: true,
  requireAllQuestions: false,
};

interface SortableQuestionCardProps {
  question: DraftQuestion;
  index: number;
  totalCount: number;
  onUpdate: (index: number, updates: Partial<DraftQuestion>) => void;
  onRemove: (index: number) => void;
  onMove: (index: number, direction: "up" | "down") => void;
}

function SortableQuestionCard({
  question,
  index,
  totalCount,
  onUpdate,
  onRemove,
  onMove,
}: SortableQuestionCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="p-4 border border-border rounded-lg bg-user-msg"
    >
      <div className="flex items-start gap-3">
        <div className="flex flex-col items-center gap-1 text-muted">
          <button
            onClick={() => onMove(index, "up")}
            disabled={index === 0}
            className="p-1 hover:text-foreground disabled:opacity-30"
            aria-label="Move question up"
            title="Move question up"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:text-foreground"
            title="Drag to reorder"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
            </svg>
          </div>
          <span className="text-xs font-medium">{index + 1}</span>
          <button
            onClick={() => onMove(index, "down")}
            disabled={index === totalCount - 1}
            className="p-1 hover:text-foreground disabled:opacity-30"
            aria-label="Move question down"
            title="Move question down"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        <div className="flex-1 space-y-3">
          <div className="flex items-start gap-2">
            <textarea
              value={question.text}
              onChange={(e) => onUpdate(index, { text: e.target.value })}
              placeholder="Enter your question..."
              rows={2}
              className="flex-1 px-3 py-2 rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-y min-h-[3rem]"
            />
            {question.aiGenerated && (
              <span className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded mt-1">
                AI
              </span>
            )}
          </div>

          <div className="flex items-center gap-4">
            <div className="relative">
              <select
                title="Question Type"
                value={question.type}
                onChange={(e) =>
                  onUpdate(index, { type: e.target.value as QuestionType })
                }
                className="appearance-none pl-3 pr-10 py-1.5 rounded-lg border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer"
              >
                {QUESTION_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <svg
                className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            <label className="flex items-center gap-2 text-sm text-muted">
              <input
                type="checkbox"
                checked={question.required}
                onChange={(e) => onUpdate(index, { required: e.target.checked })}
                className="rounded border-border"
              />
              Required
            </label>
          </div>

          {question.type === "multiple_choice" && (
            <div className="space-y-2">
              <span className="text-sm text-muted">Options</span>
              {question.options.filter((o) => o.trim()).length < 2 && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Add at least 2 options for respondents to choose from.
                </p>
              )}
              {question.options.map((option, optIndex) => (
                <div key={optIndex} className="flex items-center gap-1">
                  <div className="flex flex-col">
                    <button
                      onClick={() => {
                        if (optIndex === 0) return;
                        const newOptions = [...question.options];
                        [newOptions[optIndex - 1], newOptions[optIndex]] = [newOptions[optIndex], newOptions[optIndex - 1]];
                        onUpdate(index, { options: newOptions });
                      }}
                      disabled={optIndex === 0}
                      className="p-0.5 text-muted hover:text-foreground disabled:opacity-30"
                      aria-label="Move option up"
                      title="Move option up"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        if (optIndex === question.options.length - 1) return;
                        const newOptions = [...question.options];
                        [newOptions[optIndex], newOptions[optIndex + 1]] = [newOptions[optIndex + 1], newOptions[optIndex]];
                        onUpdate(index, { options: newOptions });
                      }}
                      disabled={optIndex === question.options.length - 1}
                      className="p-0.5 text-muted hover:text-foreground disabled:opacity-30"
                      aria-label="Move option down"
                      title="Move option down"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => {
                      const newOptions = [...question.options];
                      newOptions[optIndex] = e.target.value;
                      onUpdate(index, { options: newOptions });
                    }}
                    placeholder={`Option ${optIndex + 1}`}
                    className="flex-1 px-3 py-1.5 rounded-lg border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <button
                    onClick={() => {
                      const newOptions = question.options.filter((_, i) => i !== optIndex);
                      onUpdate(index, { options: newOptions });
                    }}
                    className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    aria-label="Remove option"
                    title="Remove option"
                  >
                    <HiOutlineTrash className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => onUpdate(index, { options: [...question.options, ""] })}
                className="text-sm text-primary hover:underline"
              >
                + Add option
              </button>
            </div>
          )}
        </div>

        <button
          onClick={() => onRemove(index)}
          className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
          aria-label="Remove question"
          title="Remove question"
        >
          <HiOutlineTrash className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

export function InterviewBuilder() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: interviewId } = useParams<{ id: string }>();
  const { authFetch } = useAuthFetch();
  const isEditing = !!interviewId;

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [questions, setQuestions] = useState<DraftQuestion[]>([]);
  const [settings, setSettings] = useState<InterviewSettings>(DEFAULT_SETTINGS);
  const [status, setStatus] = useState<InterviewStatus>("draft");
  const [shareToken, setShareToken] = useState<string>("");

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishProgress, setPublishProgress] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiContext, setAiContext] = useState("");
  const [aiCount, setAiCount] = useState(5);
  const [aiQuestionTypes, setAiQuestionTypes] = useState<QuestionType[]>([]);
  const [aiReplaceMode, setAiReplaceMode] = useState(false);
  const [hasGeneratedBefore, setHasGeneratedBefore] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLinkVerified, setIsLinkVerified] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  // Drag-and-drop sensors for question reordering
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = questions.findIndex((q) => q.id === active.id);
      const newIndex = questions.findIndex((q) => q.id === over.id);
      setQuestions(arrayMove(questions, oldIndex, newIndex));
    }
  };

  // Helper function to wait for interview status to propagate in Cosmos DB
  // Uses the public share token endpoint which cross-partition queries use
  // Returns true only when the status is verified as propagated
  const waitForStatusPropagation = useCallback(
    async (
      token: string,
      expectedStatus: InterviewStatus
    ): Promise<boolean> => {
      for (let attempt = 1; attempt <= PROPAGATION_MAX_ATTEMPTS; attempt++) {
        setPublishProgress(
          `Verifying status... (${attempt}/${PROPAGATION_MAX_ATTEMPTS})`
        );

        try {
          // Use the public respond endpoint which does the cross-partition query
          // This is the same query that participants will use
          const res = await authFetch(`${API_URL}/respond/${token}`, {
            headers: { "Cache-Control": "no-cache, no-store, must-revalidate" },
          });

          if (expectedStatus === "active") {
            if (res.ok) {
              // Parse the response to verify the actual status value
              try {
                const data = await res.json();
                // Verify the interview object exists and has the expected status
                // Note: The endpoint returns { interview: { ... } } for active interviews
                if (data.interview) {
                  console.log(
                    `[InterviewBuilder] Status propagated and verified on attempt ${attempt}`
                  );
                  return true;
                }
              } catch {
                // JSON parsing failed, treat as not propagated yet
                console.warn(
                  `[InterviewBuilder] Response parsing failed on attempt ${attempt}`
                );
              }
            }
          } else {
            // For closing/other status changes, 403 indicates the status has propagated
            if (res.status === 403) {
              console.log(
                `[InterviewBuilder] Closed status propagated on attempt ${attempt}`
              );
              return true;
            }
          }
        } catch (err) {
          console.warn(
            `[InterviewBuilder] Propagation check failed on attempt ${attempt}:`,
            err
          );
        }

        // Wait before next attempt (unless this was the last attempt)
        if (attempt < PROPAGATION_MAX_ATTEMPTS) {
          await new Promise((resolve) =>
            setTimeout(resolve, PROPAGATION_DELAY_MS)
          );
        }
      }

      console.error(
        "[InterviewBuilder] Status propagation verification timed out after all attempts"
      );
      return false;
    },
    [authFetch]
  );

  // Load existing interview if editing
  useEffect(() => {
    if (isEditing && interviewId) {
      loadInterview(interviewId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewId, isEditing]);

  // Show share modal if redirected after publishing
  useEffect(() => {
    const state = location.state as {
      showShare?: boolean;
      waitForPropagation?: boolean;
    } | null;
    if (state?.showShare && shareToken) {
      // Clear the state so it doesn't show again on refresh
      navigate(location.pathname, { replace: true, state: {} });

      // If we need to wait for propagation, do so before showing the modal
      if (state.waitForPropagation) {
        const verifyAndShow = async () => {
          setIsPublishing(true);
          setPublishProgress("Waiting for changes to propagate...");
          setIsLinkVerified(false);
          setPublishError(null);

          // Use the same propagation check logic
          const verified = await waitForStatusPropagation(shareToken, "active");

          setIsPublishing(false);
          setPublishProgress("");

          if (verified) {
            setIsLinkVerified(true);
            setPublishError(null);
          } else {
            setIsLinkVerified(false);
            setPublishError(
              "The interview was saved but may take a moment to become accessible. " +
                'Please wait a few seconds before sharing the link, or click "Retry Verification" below.'
            );
          }

          setShowShareModal(true);
        };

        verifyAndShow();
      } else {
        // Already verified or no propagation needed
        setIsLinkVerified(true);
        setShowShareModal(true);
      }
    }
  }, [
    location.state,
    shareToken,
    navigate,
    location.pathname,
    authFetch,
    waitForStatusPropagation,
  ]);

  const loadInterview = async (id: string) => {
    setIsLoading(true);
    try {
      const response = await authFetch(`${API_URL}/interviews/${id}`);
      if (response.ok) {
        const data = await response.json();
        const interview = data.interview;
        setTitle(interview.title);
        setDescription(interview.description || "");
        setQuestions(
          interview.questions.map((q: InterviewQuestion) => ({
            ...q,
            options: q.options || [],
            followUpPrompt: q.followUpPrompt || "",
          }))
        );
        setSettings(interview.settings);
        setStatus(interview.status);
        setShareToken(interview.shareToken);
      } else {
        setError("Failed to load interview");
      }
    } catch {
      setError("Failed to load interview");
    } finally {
      setIsLoading(false);
    }
  };

  // Add new question
  const addQuestion = () => {
    const newQuestion: DraftQuestion = {
      id: crypto.randomUUID(),
      text: "",
      type: "open",
      required: true,
      options: [],
      aiGenerated: false,
      followUpPrompt: "",
    };
    setQuestions([...questions, newQuestion]);
  };

  // Update question
  const updateQuestion = (index: number, updates: Partial<DraftQuestion>) => {
    const newQuestions = [...questions];
    const current = newQuestions[index];
    const merged = { ...current, ...updates };

    if (
      updates.type === "multiple_choice" &&
      current.type !== "multiple_choice" &&
      merged.options.length === 0
    ) {
      merged.options = ["", "", ""];
    }

    newQuestions[index] = merged;
    setQuestions(newQuestions);
  };

  // Remove question
  const removeQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  // Move question up/down
  const moveQuestion = (index: number, direction: "up" | "down") => {
    const newQuestions = [...questions];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= questions.length) return;
    [newQuestions[index], newQuestions[newIndex]] = [
      newQuestions[newIndex],
      newQuestions[index],
    ];
    setQuestions(newQuestions);
  };

  // Generate AI interview draft (title, description, questions)
  const generateQuestions = async () => {
    if (!aiTopic.trim()) return;

    setIsGenerating(true);
    setError(null);
    try {
      const response = await authFetch(
        `${API_URL}/interviews/ai/generate-questions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            topic: aiTopic,
            context: aiContext || undefined,
            count: aiCount,
            questionTypes: aiQuestionTypes.length > 0 ? aiQuestionTypes : undefined,
            includeTitleDescription: true,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();

        if (data.title) {
          setTitle(data.title);
        }
        if (data.description) {
          setDescription(data.description);
        }

        const newQuestions: DraftQuestion[] = data.questions.map(
          (q: InterviewQuestion) => ({
            id: q.id,
            text: q.text,
            type: q.type,
            required: true,
            options: q.options || [],
            aiGenerated: true,
            followUpPrompt: q.followUpPrompt || "",
          })
        );

        if (aiReplaceMode) {
          const manualQuestions = questions.filter((q) => !q.aiGenerated);
          setQuestions([...manualQuestions, ...newQuestions]);
        } else {
          setQuestions([...questions, ...newQuestions]);
        }

        setHasGeneratedBefore(true);
        setShowAIModal(false);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to generate questions");
      }
    } catch {
      setError("Failed to generate questions");
    } finally {
      setIsGenerating(false);
    }
  };

  // Save interview - with immediate state sync and cache busting
  const saveInterview = async (newStatus?: InterviewStatus) => {
    if (!title.trim()) {
      setError("Title is required");
      return;
    }

    const isPublishingNow = newStatus === "active";
    setIsSaving(true);
    if (isPublishingNow) {
      setIsPublishing(true);
      setPublishProgress("Publishing interview...");
    }
    setError(null);

    const mcMissingOptions = questions.some(
      (q) =>
        q.type === "multiple_choice" &&
        q.options.filter((o) => o.trim()).length < 2
    );
    if (mcMissingOptions) {
      setError(
        "Multiple choice questions need at least 2 non-empty options."
      );
      setIsSaving(false);
      setIsPublishing(false);
      setPublishProgress("");
      return;
    }

    const interviewData = {
      title,
      description: description || undefined,
      questions: questions.map((q, index) => ({
        id: q.id,
        text: q.text,
        type: q.type,
        order: index,
        required: q.required,
        options:
          q.type === "multiple_choice"
            ? q.options.map((o) => o.trim()).filter(Boolean)
            : undefined,
        aiGenerated: q.aiGenerated,
        followUpPrompt: q.followUpPrompt || undefined,
      })),
      settings,
      status: newStatus || status,
    };

    try {
      const url = isEditing
        ? `${API_URL}/interviews/${interviewId}`
        : `${API_URL}/interviews`;
      const method = isEditing ? "PATCH" : "POST";

      // Add cache-busting header for status changes
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (newStatus) {
        headers["Cache-Control"] = "no-cache, no-store, must-revalidate";
      }

      const response = await authFetch(url, {
        method,
        headers,
        body: JSON.stringify(interviewData),
      });

      if (response.ok) {
        const data = await response.json();
        const savedToken = data.interview.shareToken;

        if (!isEditing) {
          // For new interviews, if publishing, navigate and wait for propagation there
          if (newStatus === "active") {
            navigate(`/interviews/${data.interview.id}/edit`, {
              state: { showShare: true, waitForPropagation: true },
            });
          } else {
            navigate(`/interviews/${data.interview.id}/edit`);
          }
        } else {
          // Immediately update local state to reflect changes
          setStatus(data.interview.status);
          setShareToken(savedToken);
          setPublishError(null);
          setIsLinkVerified(false);

          // For status changes, wait for propagation before showing share modal
          if (newStatus === "active" || newStatus === "closed") {
            setPublishProgress("Waiting for changes to propagate...");

            // Wait for the status change to propagate to cross-partition queries
            const propagated = await waitForStatusPropagation(
              savedToken,
              newStatus
            );

            // Verify the change persisted by refetching
            try {
              const verifyRes = await authFetch(
                `${API_URL}/interviews/${interviewId}`,
                {
                  headers: { "Cache-Control": "no-cache" },
                }
              );
              if (verifyRes.ok) {
                const verifyData = await verifyRes.json();
                // Update with verified server state
                setStatus(verifyData.interview.status);
                setShareToken(verifyData.interview.shareToken);
              }
            } catch (verifyErr) {
              console.warn(
                "[InterviewBuilder] Verification fetch failed:",
                verifyErr
              );
              // Continue with optimistic update if verify fails
            }

            // Handle propagation result
            if (propagated) {
              setIsLinkVerified(true);
              setPublishError(null);
            } else {
              setIsLinkVerified(false);
              setPublishError(
                "The interview was saved but may take a moment to become accessible. " +
                  'Please wait a few seconds before sharing the link, or click "Retry Verification" below.'
              );
            }
          }

          // Show share modal after publishing (with verification status)
          if (newStatus === "active") {
            setPublishProgress("");
            setShowShareModal(true);
          }
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to save interview");
      }
    } catch {
      setError("Failed to save interview");
    } finally {
      setIsSaving(false);
      setIsPublishing(false);
      setPublishProgress("");
    }
  };

  // Copy share link - used in share modal
  const handleCopyShareLink = async () => {
    const shareUrl = `${window.location.origin}/respond/${shareToken}`;
    await navigator.clipboard.writeText(shareUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  // Open share modal with verification check for already-published interviews
  const handleOpenShareModal = async () => {
    setShowShareModal(true);
    setPublishError(null);

    // Quick verification check for already-published interviews
    try {
      const res = await authFetch(`${API_URL}/respond/${shareToken}`, {
        headers: { "Cache-Control": "no-cache, no-store, must-revalidate" },
      });

      if (res.ok) {
        const data = await res.json();
        if (data.interview) {
          setIsLinkVerified(true);
          return;
        }
      }
    } catch (err) {
      console.warn(
        "[InterviewBuilder] Share modal verification check failed:",
        err
      );
    }

    // If quick check failed, mark as unverified (user can retry)
    setIsLinkVerified(false);
    setPublishError(
      'Could not verify link accessibility. The link may still work—try copying and testing it, or click "Retry Verification" below.'
    );
  };

  // Delete interview
  const deleteInterview = async () => {
    if (!interviewId) return;

    setIsDeleting(true);
    setError(null);

    try {
      const response = await authFetch(`${API_URL}/interviews/${interviewId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        navigate("/");
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to delete interview");
        setShowDeleteModal(false);
      }
    } catch {
      setError("Failed to delete interview");
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-8 animate-fade-in">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => navigate("/")}
            className="text-muted hover:text-foreground text-sm mb-2 flex items-center gap-1"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Dashboard
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">
              {isEditing ? "Edit Interview" : "Create Interview"}
            </h1>
            {isEditing && (
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                  status === "active"
                    ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                    : status === "closed"
                    ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                }`}
              >
                {status === "active"
                  ? "Active"
                  : status === "closed"
                  ? "Closed"
                  : "Draft"}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Delete button - always visible when editing */}
          {isEditing && (
            <button
              onClick={() => setShowDeleteModal(true)}
              className="btn text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
              title="Delete Interview"
            >
              <HiOutlineTrash className="w-4 h-4" />
              <span className="hidden sm:inline">Delete</span>
            </button>
          )}

          {/* Share Link button - visible when published */}
          {isEditing && status === "active" && shareToken && (
            <button
              onClick={handleOpenShareModal}
              className="btn flex items-center gap-2 bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-500/20"
            >
              <HiOutlineShare className="w-4 h-4" />
              <span className="hidden sm:inline">Share Link</span>
            </button>
          )}

          {/* Close Interview button - visible when active */}
          {status === "active" && (
            <button
              onClick={() => saveInterview("closed")}
              disabled={isSaving}
              className="btn flex items-center gap-2 bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20"
              title="Stop accepting responses"
            >
              {isSaving ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <HiOutlineXCircle className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Close</span>
            </button>
          )}

          {/* Reopen button - visible when closed */}
          {status === "closed" && (
            <button
              onClick={() => saveInterview("active")}
              disabled={isSaving}
              className="btn flex items-center gap-2 bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-500/20"
              title="Start accepting responses again"
            >
              {isSaving ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <HiOutlineArrowPath className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Reopen</span>
            </button>
          )}

          {/* Save button */}
          <button
            onClick={() => saveInterview()}
            disabled={isSaving}
            className="btn flex items-center gap-2"
          >
            {isSaving && (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            )}
            {status === "active"
              ? "Save Changes"
              : status === "closed"
              ? "Save Changes"
              : "Save Draft"}
          </button>

          {/* Publish button - visible when draft and has questions */}
          {status === "draft" && questions.length > 0 && (
            <button
              onClick={() => saveInterview("active")}
              disabled={isSaving}
              className="btn btn-primary flex items-center gap-2"
            >
              Publish
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Title & Description with AI Draft button */}
      <div className="card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">
            Interview Details
          </h2>
          <button
            onClick={() => setShowAIModal(true)}
            className="btn flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white border-0"
          >
            <HiOutlineSparkles className="w-4 h-4" />
            Draft with AI
          </button>
        </div>
        <div className="mb-4">
          <label
            htmlFor="interview-title"
            className="block text-sm font-medium text-foreground mb-1"
          >
            Interview Title *
          </label>
          <input
            id="interview-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Q1 Process Improvement Feedback"
            className="w-full px-4 py-2 rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
        <div>
          <label
            htmlFor="interview-description"
            className="block text-sm font-medium text-foreground mb-1"
          >
            Description (optional)
          </label>
          <textarea
            id="interview-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of what this interview is about..."
            rows={2}
            className="w-full px-4 py-2 rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
          />
        </div>
      </div>

      {/* Questions */}
      <div className="card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Questions</h2>
          <button
            onClick={addQuestion}
            className="btn btn-primary flex items-center gap-2 text-sm"
          >
            <HiOutlinePlus className="w-4 h-4" />
            Add Question
          </button>
        </div>

        {questions.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
            <HiOutlineSparkles className="w-10 h-10 mx-auto text-purple-500 mb-3" />
            <p className="text-foreground font-medium mb-1">No questions yet</p>
            <p className="text-muted text-sm mb-4">
              Use "Draft with AI" above to generate title, description and
              questions, or add them manually.
            </p>
            <button
              onClick={addQuestion}
              className="btn btn-primary flex items-center gap-2 mx-auto"
            >
              <HiOutlinePlus className="w-4 h-4" />
              Add Question Manually
            </button>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={questions.map((q) => q.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-4">
                {questions.map((question, index) => (
                  <SortableQuestionCard
                    key={question.id}
                    question={question}
                    index={index}
                    totalCount={questions.length}
                    onUpdate={updateQuestion}
                    onRemove={removeQuestion}
                    onMove={moveQuestion}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Settings */}
      <div className="card">
        <h2 className="text-lg font-semibold text-foreground mb-4">Settings</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-user-msg">
            <input
              type="checkbox"
              checked={settings.allowVoice}
              onChange={(e) =>
                setSettings({ ...settings, allowVoice: e.target.checked })
              }
              className="rounded border-border"
            />
            <span>
              <span className="font-medium text-foreground block">
                Allow Voice
              </span>
              <span className="text-xs text-muted">
                Participants can respond using voice
              </span>
            </span>
          </label>

          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-user-msg">
            <input
              type="checkbox"
              checked={settings.allowChat}
              onChange={(e) =>
                setSettings({ ...settings, allowChat: e.target.checked })
              }
              className="rounded border-border"
            />
            <span>
              <span className="font-medium text-foreground block">
                Allow Chat
              </span>
              <span className="text-xs text-muted">
                Participants can chat with AI interviewer
              </span>
            </span>
          </label>

          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-user-msg">
            <input
              type="checkbox"
              checked={settings.allowText}
              onChange={(e) =>
                setSettings({ ...settings, allowText: e.target.checked })
              }
              className="rounded border-border"
            />
            <span>
              <span className="font-medium text-foreground block">
                Allow Text
              </span>
              <span className="text-xs text-muted">
                Participants can type responses
              </span>
            </span>
          </label>

          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-user-msg">
            <input
              type="checkbox"
              checked={settings.allowFileUpload}
              onChange={(e) =>
                setSettings({ ...settings, allowFileUpload: e.target.checked })
              }
              className="rounded border-border"
              aria-describedby="upload-desc"
            />
            <span>
              <span className="font-medium text-foreground block">
                Allow File Upload
              </span>
              <span id="upload-desc" className="text-xs text-muted">
                Participants can attach files
              </span>
            </span>
          </label>
        </div>
      </div>

      {/* AI Draft Modal */}
      {showAIModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            role="button"
            tabIndex={0}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowAIModal(false)}
            onKeyDown={(e) => e.key === "Escape" && setShowAIModal(false)}
            aria-label="Close modal"
          />
          <div className="relative bg-card border border-border rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-fade-in">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/10 flex items-center justify-center">
                  <HiOutlineSparkles className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    Draft Interview with AI
                  </h3>
                  <p className="text-sm text-muted">
                    AI will generate title, description & questions
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="ai-topic"
                    className="block text-sm font-medium text-foreground mb-1"
                  >
                    Topic / Goal *
                  </label>
                  <input
                    id="ai-topic"
                    type="text"
                    value={aiTopic}
                    onChange={(e) => setAiTopic(e.target.value)}
                    placeholder="e.g., Understand customer onboarding pain points"
                    className="w-full px-4 py-2 rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                  />
                </div>

                <div>
                  <label
                    htmlFor="ai-context"
                    className="block text-sm font-medium text-foreground mb-1"
                  >
                    Additional Context (optional)
                  </label>
                  <textarea
                    id="ai-context"
                    value={aiContext}
                    onChange={(e) => setAiContext(e.target.value)}
                    placeholder="Any specific areas to focus on, audience details, etc."
                    rows={3}
                    className="w-full px-4 py-2 rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-purple-500/50 resize-none"
                  />
                </div>

                <div className="flex items-end gap-4">
                  <div>
                    <label
                      htmlFor="ai-question-count"
                      className="block text-sm font-medium text-foreground mb-1"
                    >
                      Number of Questions
                    </label>
                    <input
                      id="ai-question-count"
                      type="number"
                      min={1}
                      max={20}
                      value={aiCount}
                      onChange={(e) => setAiCount(Number(e.target.value))}
                      className="w-24 px-4 py-2 rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                    />
                  </div>

                  <div className="flex-1">
                    <label
                      htmlFor="ai-question-type"
                      className="block text-sm font-medium text-foreground mb-1"
                    >
                      Question Type
                    </label>
                    <select
                      id="ai-question-type"
                      title="Question type filter"
                      value={aiQuestionTypes.length === 1 ? aiQuestionTypes[0] : ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setAiQuestionTypes(val ? [val as QuestionType] : []);
                      }}
                      className="w-full px-3 py-2 rounded-lg border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                    >
                      <option value="">Mix (all types)</option>
                      {QUESTION_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label} only</option>
                      ))}
                    </select>
                  </div>
                </div>

                {hasGeneratedBefore && questions.some((q) => q.aiGenerated) && (
                  <label className="flex items-center gap-2 text-sm text-muted">
                    <input
                      type="checkbox"
                      checked={aiReplaceMode}
                      onChange={(e) => setAiReplaceMode(e.target.checked)}
                      className="rounded border-border"
                    />
                    Replace previous AI-generated questions
                  </label>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowAIModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg bg-user-msg text-foreground hover:bg-border transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={generateQuestions}
                  disabled={isGenerating || !aiTopic.trim()}
                  className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isGenerating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <HiOutlineSparkles className="w-4 h-4" />
                      Generate
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Publishing Progress Overlay */}
      {isPublishing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-card border border-border rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-fade-in">
            <div className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center">
                <HiOutlineArrowPath className="w-8 h-8 text-primary animate-spin" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Publishing Interview
              </h3>
              <p className="text-sm text-muted">
                {publishProgress || "Please wait..."}
              </p>
              <div className="mt-4 w-full bg-border rounded-full h-1.5 overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full animate-pulse"
                  style={{ width: "60%" }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Link Modal */}
      {showShareModal && shareToken && !isPublishing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            role="button"
            tabIndex={0}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowShareModal(false)}
            onKeyDown={(e) => e.key === "Escape" && setShowShareModal(false)}
            aria-label="Close modal"
          />
          <div className="relative bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    isLinkVerified ? "bg-green-500/20" : "bg-yellow-500/20"
                  }`}
                >
                  {isLinkVerified ? (
                    <HiOutlineCheckCircle className="w-7 h-7 text-green-500" />
                  ) : (
                    <HiOutlineArrowPath className="w-7 h-7 text-yellow-500" />
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground">
                    {isLinkVerified
                      ? "Interview Published!"
                      : "Interview Saved"}
                  </h3>
                  <p className="text-sm text-muted">
                    {isLinkVerified
                      ? "Share this link with participants"
                      : "Verifying link availability..."}
                  </p>
                </div>
              </div>

              {/* Status confirmation badge */}
              {isLinkVerified ? (
                <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    <span className="text-sm font-medium">
                      Interview is live and accepting responses
                    </span>
                  </div>
                </div>
              ) : (
                <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 mb-2">
                    <div className="w-2 h-2 rounded-full bg-yellow-500" />
                    <span className="text-sm font-medium">
                      Link not yet verified
                    </span>
                  </div>
                  <p className="text-xs text-muted">
                    {publishError ||
                      "The interview is saved but the share link may take a moment to become active. Click retry to check again."}
                  </p>
                </div>
              )}

              <div className="mb-4">
                <label
                  htmlFor="share-link"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  Share Link
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="share-link"
                    type="text"
                    readOnly
                    value={`${window.location.origin}/respond/${shareToken}`}
                    className={`flex-1 px-4 py-3 rounded-lg border bg-user-msg text-foreground text-sm font-mono focus:outline-none ${
                      isLinkVerified ? "border-border" : "border-yellow-500/50"
                    }`}
                  />
                  <button
                    onClick={handleCopyShareLink}
                    disabled={!isLinkVerified}
                    className={`px-4 py-3 rounded-lg transition-colors flex items-center gap-2 ${
                      isLinkVerified
                        ? "bg-primary text-white hover:bg-primary-hover"
                        : "bg-muted text-muted-foreground cursor-not-allowed"
                    }`}
                    title={
                      isLinkVerified ? "Copy link" : "Please verify link first"
                    }
                  >
                    {copiedLink ? (
                      <>
                        <HiOutlineCheckCircle className="w-5 h-5" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <HiOutlineShare className="w-5 h-5" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Retry verification button when not verified */}
              {!isLinkVerified && (
                <div className="mb-4">
                  <button
                    onClick={async () => {
                      setPublishError(null);
                      setPublishProgress("Retrying verification...");
                      const verified = await waitForStatusPropagation(
                        shareToken,
                        "active"
                      );
                      setPublishProgress("");
                      if (verified) {
                        setIsLinkVerified(true);
                        setPublishError(null);
                      } else {
                        setPublishError(
                          "Verification timed out. The link may still work - please try again in a moment."
                        );
                      }
                    }}
                    disabled={!!publishProgress}
                    className="w-full px-4 py-2.5 rounded-lg bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-500/30 transition-colors font-medium flex items-center justify-center gap-2"
                  >
                    <HiOutlineArrowPath
                      className={`w-5 h-5 ${
                        publishProgress ? "animate-spin" : ""
                      }`}
                    />
                    {publishProgress || "Retry Verification"}
                  </button>
                </div>
              )}

              {/* Invite People */}
              {isLinkVerified && (
                <div className="mb-4">
                  <button
                    onClick={() => {
                      setShowShareModal(false);
                      setShowInviteModal(true);
                    }}
                    className="w-full px-4 py-2.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors font-medium flex items-center justify-center gap-2"
                  >
                    <HiOutlineEnvelope className="w-5 h-5" />
                    Invite People via Email
                  </button>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setShowShareModal(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-user-msg text-foreground hover:bg-border transition-colors font-medium"
                >
                  Close
                </button>
                <button
                  onClick={() => navigate(`/interviews/${interviewId}/results`)}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-primary text-white hover:bg-primary-hover transition-colors font-medium"
                >
                  View Results
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {interviewId && shareToken && (
        <InviteModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          interviewId={interviewId}
          interviewTitle={title}
          shareToken={shareToken}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            role="button"
            tabIndex={0}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => !isDeleting && setShowDeleteModal(false)}
            onKeyDown={(e) =>
              e.key === "Escape" && !isDeleting && setShowDeleteModal(false)
            }
            aria-label="Close modal"
          />
          <div className="relative bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                  <HiOutlineTrash className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground">
                    Delete Interview?
                  </h3>
                  <p className="text-sm text-muted">
                    This action cannot be undone
                  </p>
                </div>
              </div>

              <p className="text-foreground mb-6">
                Are you sure you want to delete "
                <strong>{title || "this interview"}</strong>"?
                {status === "active" &&
                  " This interview is currently published and accepting responses."}{" "}
                All responses will also be deleted.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-user-msg text-foreground hover:bg-border transition-colors font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={deleteInterview}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isDeleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <HiOutlineTrash className="w-4 h-4" />
                      Delete Interview
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
